import { z } from 'zod'

import type { Service } from '@/api/services'

import { SERVICE_CATEGORY_LIST } from './services.constants'
import { sortServices, sortSkus } from './services.utils'

export const ServicesSelectionSchema = z.object({
  category: z.string().nullable(),
  serviceId: z.number().nullable(),
  skuId: z.number().nullable(),
})

export type ServicesSelection = z.infer<typeof ServicesSelectionSchema>

export const EMPTY_SERVICES_SELECTION: ServicesSelection = {
  category: null,
  serviceId: null,
  skuId: null,
}

export const parseStoredServicesSelection = (value: unknown): ServicesSelection => {
  const result = ServicesSelectionSchema.safeParse(value)
  return result.success ? result.data : EMPTY_SERVICES_SELECTION
}

interface ResolveServicesSelectionParams {
  services: Service[]
  stored: ServicesSelection
}

const pickId = <T extends { id: number }>(items: T[], preferredId: number | null) =>
  items.find(({ id }) => id === preferredId)?.id ?? items[0]?.id ?? null

// Note: a stored value that no longer exists falls back to the first item of its level; with no stored
// category, the first category that has services wins so the page does not open on an empty list
export const resolveServicesSelection = ({
  services,
  stored,
}: ResolveServicesSelectionParams): ServicesSelection => {
  const category =
    SERVICE_CATEGORY_LIST.find((item) => item === stored.category) ??
    SERVICE_CATEGORY_LIST.find((item) => services.some((service) => service.category === item)) ??
    SERVICE_CATEGORY_LIST[0] ??
    null

  const categoryServices = sortServices(services.filter((service) => service.category === category))
  const serviceId = pickId(categoryServices, stored.serviceId)
  const service = categoryServices.find(({ id }) => id === serviceId)
  const skuId = pickId(sortSkus(service?.sku ?? []), stored.skuId)

  return { category, serviceId, skuId }
}
